import { viewerStrings } from '../copy/strings.js';
import { message, hasText, optionalText } from '../copy/text.js';
import type { ClassificationRule } from '../state/rules-state.js';
import { RuleCondition } from './RuleCondition.js';

export function ClassificationRuleEditor({
  rule,
  position,
  count,
  change,
  remove,
  move,
}: {
  rule: ClassificationRule;
  position: number;
  count: number;
  change: (rule: ClassificationRule) => void;
  remove: () => void;
  move: (offset: number) => void;
}) {
  return (
    <fieldset>
      {hasText(viewerStrings.webComponentsClassificationRuleEditor.rule) ? (
        <legend>
          {message(viewerStrings.webComponentsClassificationRuleEditor.rule, {
            value1: position + 1,
          })}
        </legend>
      ) : null}
      <div className="field-tools" data-copy-group>
        <label data-copy-label>
          {hasText(viewerStrings.webComponentsClassificationRuleEditor.name) ? (
            <span>{viewerStrings.webComponentsClassificationRuleEditor.name}</span>
          ) : null}
          <input
            aria-label={optionalText(
              viewerStrings.webAccessibility.componentsClassificationRuleEditor1name,
            )}
            value={rule.name}
            maxLength={200}
            required
            onChange={(event) => change({ ...rule, name: event.target.value })}
          />
        </label>
        <label data-copy-label>
          {hasText(viewerStrings.webComponentsClassificationRuleEditor.enabled) ? (
            <span>{viewerStrings.webComponentsClassificationRuleEditor.enabled}</span>
          ) : null}
          <input
            aria-label={optionalText(
              viewerStrings.webAccessibility.componentsClassificationRuleEditor2enabled,
            )}
            type="checkbox"
            checked={rule.enabled}
            onChange={(event) => change({ ...rule, enabled: event.target.checked })}
          />
        </label>
        {hasText(viewerStrings.webComponentsClassificationRuleEditor.moveUp) ? (
          <button type="button" disabled={position === 0} onClick={() => move(-1)}>
            {viewerStrings.webComponentsClassificationRuleEditor.moveUp}
          </button>
        ) : null}
        {hasText(viewerStrings.webComponentsClassificationRuleEditor.moveDown) ? (
          <button type="button" disabled={position === count - 1} onClick={() => move(1)}>
            {viewerStrings.webComponentsClassificationRuleEditor.moveDown}
          </button>
        ) : null}
        {hasText(viewerStrings.webComponentsClassificationRuleEditor.deleteRule) ? (
          <button type="button" onClick={remove}>
            {viewerStrings.webComponentsClassificationRuleEditor.deleteRule}
          </button>
        ) : null}
      </div>
      <div className="field-tools" data-copy-group>
        <label data-copy-label>
          {hasText(viewerStrings.webComponentsClassificationRuleEditor.endpoint) ? (
            <span>{viewerStrings.webComponentsClassificationRuleEditor.endpoint}</span>
          ) : null}
          <select
            aria-label={optionalText(
              viewerStrings.webAccessibility.componentsClassificationRuleEditor3endpoint,
            )}
            value={rule.endpoint}
            onChange={(event) =>
              change({ ...rule, endpoint: event.target.value as ClassificationRule['endpoint'] })
            }
          >
            {hasText(viewerStrings.webComponentsClassificationRuleEditor.both) ? (
              <option value="both">
                {viewerStrings.webComponentsClassificationRuleEditor.both}
              </option>
            ) : null}
            {hasText(viewerStrings.webComponentsClassificationRuleEditor.destination) ? (
              <option value="destination">
                {viewerStrings.webComponentsClassificationRuleEditor.destination}
              </option>
            ) : null}
            {hasText(viewerStrings.webComponentsClassificationRuleEditor.origin) ? (
              <option value="origin">
                {viewerStrings.webComponentsClassificationRuleEditor.origin}
              </option>
            ) : null}
          </select>
        </label>
        <label data-copy-label>
          {hasText(viewerStrings.webComponentsClassificationRuleEditor.category) ? (
            <span>{viewerStrings.webComponentsClassificationRuleEditor.category}</span>
          ) : null}
          <select
            aria-label={optionalText(
              viewerStrings.webAccessibility.componentsClassificationRuleEditor4category,
            )}
            value={rule.category}
            onChange={(event) =>
              change({ ...rule, category: event.target.value as ClassificationRule['category'] })
            }
          >
            {hasText(viewerStrings.webComponentsClassificationRuleEditor.external) ? (
              <option value="external">
                {viewerStrings.webComponentsClassificationRuleEditor.external}
              </option>
            ) : null}
            {hasText(viewerStrings.webComponentsClassificationRuleEditor.internal) ? (
              <option value="internal">
                {viewerStrings.webComponentsClassificationRuleEditor.internal}
              </option>
            ) : null}
            {hasText(viewerStrings.webComponentsClassificationRuleEditor.unknown) ? (
              <option value="unknown">
                {viewerStrings.webComponentsClassificationRuleEditor.unknown}
              </option>
            ) : null}
          </select>
        </label>
        <label data-copy-label>
          {hasText(viewerStrings.webComponentsClassificationRuleEditor.label) ? (
            <span>{viewerStrings.webComponentsClassificationRuleEditor.label}</span>
          ) : null}
          <input
            aria-label={optionalText(
              viewerStrings.webAccessibility.componentsClassificationRuleEditor5label,
            )}
            value={rule.label ?? ''}
            maxLength={200}
            placeholder={optionalText(viewerStrings.webComponentsClassificationRuleEditor.optional)}
            onChange={(event) => change({ ...rule, label: event.target.value || null })}
          />
        </label>
      </div>
      {hasText(viewerStrings.webComponentsClassificationRuleEditor.matchAllConditions) ? (
        <p>{viewerStrings.webComponentsClassificationRuleEditor.matchAllConditions}</p>
      ) : null}
      {rule.conditions.map((condition, index) => (
        <RuleCondition
          key={index}
          condition={condition}
          change={(next) =>
            change({
              ...rule,
              conditions: rule.conditions.map((item, i) => (i === index ? next : item)),
            })
          }
          remove={() =>
            change({ ...rule, conditions: rule.conditions.filter((_, i) => i !== index) })
          }
        />
      ))}
      {hasText(viewerStrings.webComponentsClassificationRuleEditor.addCondition) ? (
        <button
          type="button"
          disabled={rule.conditions.length >= 16}
          onClick={() =>
            change({
              ...rule,
              conditions: [...rule.conditions, { field: 'number', op: 'eq', value: '' }],
            })
          }
        >
          {viewerStrings.webComponentsClassificationRuleEditor.addCondition}
        </button>
      ) : null}
    </fieldset>
  );
}
